import { getContaminationRate } from "@/pages/contamination/utils";

import { sortBy } from "lodash";
import type { MinimalCustomer } from "server/coreApi/customers/types.js";
import type { GetCustomersResponse } from "server/dwhApi/customers/models.js";
import type { Customer } from "server/dwhApi/customers/types.js";

import { METRIC_CONFIG } from "./constants";
import {
  BaseTenant,
  CustomerWithPosition,
  Direction,
  GroupedCustomers,
  LeaderboardRow,
  Metric,
  RankedTenant,
} from "./types";

const toBaseTenant = (customer: Customer, idx: number): BaseTenant => ({
  id: customer.customerId,
  name: customer.customerName,
  category: customer.commercialCustomerCategory,
  rate: customer.sortingDegree * 100,
  general: customer.totalResidualWasteWeight.quantity,
  contamination: getContaminationRate(customer),
  idx,
  isDeleted: customer.isDeleted,
});

const getCustomerMetricValue = (customer: Customer, metric: Metric) => {
  if (metric === Metric.GENERAL_WASTE) {
    return customer.totalResidualWasteWeight.quantity;
  }

  if (metric === Metric.RECYCLING_RATE) {
    const customerRecycledWaste =
      customer.totalWasteWeight.quantity -
      customer.totalResidualWasteWeight.quantity;

    return customer.totalWasteWeight.quantity
      ? (customerRecycledWaste * 100) / customer.totalWasteWeight.quantity
      : 0;
  }

  if (metric === Metric.CONTAMINATION) {
    return getContaminationRate(customer);
  }

  return 0;
};

const getSortedGroupedCustomers = (customers: Customer[], metric: Metric) => {
  const { higherBetter } = METRIC_CONFIG[metric];

  return customers
    .reduce((acc, customer) => {
      const metricValue = getCustomerMetricValue(customer, metric);
      const existingIndex = acc.findIndex(
        (el) => el.metricValue === metricValue,
      );

      if (existingIndex >= 0) {
        acc[existingIndex].customers.push(customer);
        return acc;
      } else {
        acc.push({ metricValue, customers: [customer] });
      }

      return acc;
    }, [] as GroupedCustomers[])
    .sort((a, b) =>
      higherBetter
        ? b.metricValue - a.metricValue
        : a.metricValue - b.metricValue,
    );
};

const getRankingPosition = (
  data: GroupedCustomers[],
  index: number,
): number => {
  const previousPositions = data.slice(0, index);
  const currentPosition =
    previousPositions.reduce((acc, group) => {
      return acc + group.customers.length;
    }, 0) + 1;

  return currentPosition;
};

const getFlatCustomersWithPosition = (
  activeCustomers: Customer[],
  metric: Metric,
) => {
  const sortedGroupedCustomers = getSortedGroupedCustomers(
    activeCustomers,
    metric,
  );

  const flatCustomersWithPosition = sortedGroupedCustomers.reduce(
    (acc, groupedCustomer, index) => {
      sortBy(groupedCustomer.customers, "customerName").forEach((c) => {
        acc.push({
          ...c,
          position: getRankingPosition(sortedGroupedCustomers, index),
        });
      });

      return acc;
    },
    [] as CustomerWithPosition[],
  );

  return flatCustomersWithPosition;
};

export const getLeaderboardRows = ({
  currentCustomers,
  comparisonCustomers,
  metric,
  direction,
  hideFullRate = false,
}: {
  currentCustomers: Customer[];
  comparisonCustomers: Customer[];
  metric: Metric;
  direction: Direction;
  hideFullRate?: boolean;
}): LeaderboardRow[] => {
  const filteredCurrentCustomers =
    metric === Metric.RECYCLING_RATE && hideFullRate
      ? currentCustomers.filter((c) => c.sortingDegree < 1)
      : currentCustomers;

  const currentPositions = getFlatCustomersWithPosition(
    filteredCurrentCustomers,
    metric,
  );

  const comparisonPositions = getFlatCustomersWithPosition(
    comparisonCustomers,
    metric,
  );
  const comparisonPositionById = new Map(
    comparisonPositions.map((c) => [c.customerId, c.position]),
  );

  const ranked: RankedTenant[] = currentPositions.map((c, idx) => {
    const tenant = toBaseTenant(c, idx);
    const comparisonPosition = comparisonPositionById.get(c.customerId);

    return {
      ...tenant,
      position: c.position,
      mv: comparisonPosition != null ? comparisonPosition - c.position : 0,
    };
  });

  const n = ranked.length;

  return direction === "top"
    ? ranked
        .slice(0, 5)
        .map((t) => ({ tenant: t, rank: t.position, top: true }))
    : ranked.slice(Math.max(0, n - 5)).map((t) => ({
        tenant: t,
        rank: t.position,
        top: false,
      }));
};

export const getCustomersWithoutCleaners = ({
  data,
  minimalCustomerList,
}: {
  data?: GetCustomersResponse;
  minimalCustomerList: MinimalCustomer[];
}) =>
  (data?.customers ?? []).filter(
    (c) =>
      minimalCustomerList.find((mc) => mc.id === c.customerId)
        ?.throwDelegate !== "true",
  );
